import CloseIconButton from "@entrilia/shared/components/CloseIconButton.tsx";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import CurrencyIcon from "@entrilia/shared/icons/CurrencyIcon";
import NumbersIcon from "@entrilia/shared/icons/NumbersIcon";
import PercentageIcon from "@entrilia/shared/icons/PercentageIcon";
import { getUserLocaleCode } from "@entrilia/shared/utilities/userLocaleConfig.ts";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import type {
  NegativeValueAs,
  NumberFormatterOptions,
  ZeroValueAs,
} from "../../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { CurrencySource, FormatType } from "../../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { CurrencyDimensionMeta } from "../../../../../../../../../../api/types/svTypes.ts";
import { formatNumber } from "../../../../../../formatters/NumberFormatter.ts";
import CurrencyCustomFormat from "./CurrencyCustomFormat.tsx";
import { CustomFormatOption } from "./FormatType.ts";
import FormatTypeItem from "./FormatTypeItem.tsx";
import { currencies } from "./currencyCodes.ts";
import Abbreviation from "./parts/Abbreviation.tsx";
import Decimals from "./parts/Decimals.tsx";
import NegativeValue from "./parts/NegativeValue.tsx";
import ZeroValue from "./parts/ZeroValue.tsx";

const defaultCurrency = "USD";
const defaultCurrencyCode = currencies.find((currency) => currency.code === defaultCurrency)?.code || "";

interface Props {
  formatOptions: NumberFormatterOptions;
  currencyDimensions: CurrencyDimensionMeta[];
  applyFormat: (formatOptions: NumberFormatterOptions) => void;
  close: () => void;
}

const getSanitizedFormatOptionsWithDefaultValues = (
  formatOptions: NumberFormatterOptions,
  formatType: CustomFormatOption
): NumberFormatterOptions => {
  const sanitizedOptions: Partial<NumberFormatterOptions> = {
    type: formatOptions.type,
  };
  switch (formatType) {
    case CustomFormatOption.Currency: {
      sanitizedOptions.formatType = FormatType.Currency;
      sanitizedOptions.referenceDimensionName = undefined;
      sanitizedOptions.currencySource = CurrencySource.Static;
      sanitizedOptions.currency = defaultCurrencyCode;
      sanitizedOptions.decimals = 2;
      break;
    }
    case CustomFormatOption.Number: {
      sanitizedOptions.formatType = FormatType.Numeric;
      sanitizedOptions.decimals = 2;
      break;
    }
    case CustomFormatOption.Percentage: {
      sanitizedOptions.formatType = FormatType.Numeric;
      sanitizedOptions.percentage = true;
      sanitizedOptions.decimals = 2;
      break;
    }
    default: {
      break;
    }
  }
  return sanitizedOptions as NumberFormatterOptions;
};

const getInitialState = (
  formatOptions: NumberFormatterOptions
): {
  selectedFormatType: CustomFormatOption;
  sampleFormatOptions: NumberFormatterOptions;
} => {
  const initialFormatType =
    formatOptions?.formatType === FormatType.Currency
      ? CustomFormatOption.Currency
      : formatOptions?.percentage === true || formatOptions?.convertToPercentage === true
        ? CustomFormatOption.Percentage
        : CustomFormatOption.Number;
  return {
    selectedFormatType: initialFormatType,
    sampleFormatOptions: { ...formatOptions } as NumberFormatterOptions,
  };
};

export const MeasureCustomFormatPopup = ({ formatOptions, currencyDimensions, applyFormat, close }: Props) => {
  const [state, setState] = useState(() => getInitialState(formatOptions));

  const exampleFormattedValue = useMemo(
    () =>
      formatNumber(
        -123456789.10123,
        state.sampleFormatOptions.formatType === FormatType.Currency &&
          state.sampleFormatOptions.currencySource === CurrencySource.Field
          ? { ...state.sampleFormatOptions, currency: "USD" }
          : state.sampleFormatOptions,
        getUserLocaleCode()
      ),
    [state.sampleFormatOptions]
  );

  const changeFormatType = (formatType: CustomFormatOption) => {
    setState((prev) => ({
      ...prev,
      selectedFormatType: formatType,
      sampleFormatOptions: getSanitizedFormatOptionsWithDefaultValues(prev.sampleFormatOptions, formatType),
    }));
  };

  const handlePrefixChange = (prefix: string) => {
    setState((prev) => ({ ...prev, sampleFormatOptions: { ...prev.sampleFormatOptions, prefix } }));
  };

  const handleSuffixChange = (suffix: string) => {
    setState((prev) => ({ ...prev, sampleFormatOptions: { ...prev.sampleFormatOptions, suffix } }));
  };

  const handleAbbreviationChange = (options: NumberFormatterOptions) => {
    setState((prev) => ({ ...prev, sampleFormatOptions: { ...prev.sampleFormatOptions, ...options } }));
  };

  const handleDecimalsChange = (options: NumberFormatterOptions) => {
    setState((prev) => ({ ...prev, sampleFormatOptions: { ...prev.sampleFormatOptions, ...options } }));
  };

  const handleAutoDecimalsChange = (autoDecimals: boolean) => {
    setState((prev) => ({ ...prev, sampleFormatOptions: { ...prev.sampleFormatOptions, autoDecimals, decimals: 10 } }));
  };

  const handleConvertToPercentageChange = (convertToPercentage: boolean) => {
    setState((prev) => ({ ...prev, sampleFormatOptions: { ...prev.sampleFormatOptions, convertToPercentage } }));
  };

  const handleReferenceDimensionNameChange = (referenceDimensionName: string) => {
    setState((prev) => ({ ...prev, sampleFormatOptions: { ...prev.sampleFormatOptions, referenceDimensionName } }));
  };

  const handleCurrencySourceChange = (currencySource: CurrencySource) => {
    if (currencySource === CurrencySource.Field) {
      if (currencyDimensions.length === 0) {
        return;
      }

      setState((prev) => ({
        ...prev,
        sampleFormatOptions: {
          ...prev.sampleFormatOptions,
          currencySource,
          referenceDimensionName: currencyDimensions[0]?.name,
          currency: "",
        },
      }));
      return;
    }
    if (currencySource === CurrencySource.Static) {
      setState((prev) => ({
        ...prev,
        sampleFormatOptions: {
          ...prev.sampleFormatOptions,
          currencySource,
          currency: defaultCurrencyCode,
          referenceDimensionName: undefined,
        },
      }));
    }
  };

  const handleCurrencySymbolChange = (currencySymbol: string) => {
    setState((prev) => ({ ...prev, sampleFormatOptions: { ...prev.sampleFormatOptions, currency: currencySymbol } }));
  };

  const handleNegativeValueChanged = (showAs: NegativeValueAs) => {
    setState((prev) => ({
      ...prev,
      sampleFormatOptions: { ...prev.sampleFormatOptions, showNegativeValuesAs: showAs },
    }));
  };

  const handleZeroValueChanged = (showAs: ZeroValueAs) => {
    setState((prev) => ({ ...prev, sampleFormatOptions: { ...prev.sampleFormatOptions, showZeroValuesAs: showAs } }));
  };

  return (
    <Dialog open={true} maxWidth="sm" fullWidth onClose={close} slotProps={{ paper: { sx: { borderRadius: "8px" } } }}>
      <Grid
        container
        sx={{ alignItems: "center", justifyContent: "stretch", p: "1rem 1rem 0rem 1.5rem", width: "100%" }}
      >
        <Typography variant="h6" sx={{ flex: 1, color: "text.primary" }}>
          Custom Format
        </Typography>
        <CloseIconButton
          onClick={close}
          iconProps={{ fontSize: "medium", sx: (theme) => ({ color: theme.palette.text.secondary }) }}
        />
      </Grid>
      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ justifyContent: "stretch", px: "1.5rem", pb: 2, pt: 1, width: "100%" }}>
          <TextField
            label="Example"
            disabled
            value={exampleFormattedValue}
            sx={(theme) => ({
              flex: 1,
              backgroundColor: theme.palette.background.grey,
              WebkitTextFillColor: theme.palette.text.secondary,
              fieldset: {
                borderColor: `${theme.palette.divider} !important`,
              },
            })}
          />
        </Grid>
        <Divider />
        <Box sx={{ width: "100%", display: "flex", minHeight: "24rem" }}>
          <Box sx={{ display: "flex", flexDirection: "column", p: "1rem .5rem", flex: 0.45 }}>
            <RadioGroup
              value={state.selectedFormatType}
              sx={{ pl: "1.5rem", pr: ".5rem" }}
              onChange={(_, value) => changeFormatType(value as CustomFormatOption)}
            >
              <FormatTypeItem
                formatType={CustomFormatOption.Number}
                currentFormatType={state.selectedFormatType}
                icon={<NumbersIcon />}
              />
              <FormatTypeItem
                formatType={CustomFormatOption.Percentage}
                currentFormatType={state.selectedFormatType}
                icon={<PercentageIcon />}
              />
              <FormatTypeItem
                formatType={CustomFormatOption.Currency}
                currentFormatType={state.selectedFormatType}
                icon={<CurrencyIcon />}
              />
            </RadioGroup>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Stack useFlexGap spacing=".5rem" sx={{ p: "1.3rem", width: "310px", minHeight: "310px", flex: 0.65 }}>
            {state.selectedFormatType === CustomFormatOption.Number && (
              <>
                <Stack direction="row" useFlexGap spacing={2}>
                  <TextField
                    label="Prefix"
                    sx={{ pb: 1 }}
                    value={state.sampleFormatOptions.prefix || ""}
                    onChange={(evt) => handlePrefixChange(evt.target.value)}
                  />
                  <TextField
                    label="Suffix"
                    sx={{ pb: 1 }}
                    value={state.sampleFormatOptions.suffix || ""}
                    onChange={(evt) => handleSuffixChange(evt.target.value)}
                  />
                </Stack>
                <Abbreviation formatOptions={state.sampleFormatOptions} applyFormat={handleAbbreviationChange} />
                <Box sx={{ display: "flex", pt: ".5rem", gap: ".5rem" }}>
                  <Decimals formatOptions={state.sampleFormatOptions} applyFormat={handleDecimalsChange} />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "0" }}>
                  <FormControlLabel
                    sx={{ mt: "-5px" }}
                    control={<Checkbox checked={state.sampleFormatOptions.autoDecimals === true} />}
                    label={<Typography>Auto Decimal Places</Typography>}
                    onChange={(_, value) => handleAutoDecimalsChange(value)}
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary", mt: "-5px" }}>
                    Auto Decimal Places will display up to 10 decimal places and remove trailing zeros.
                  </Typography>
                </Box>
                <NegativeValue
                  onNegativeValueChanged={handleNegativeValueChanged}
                  value={state.sampleFormatOptions.showNegativeValuesAs}
                />
                <ZeroValue
                  onZeroValueChanged={handleZeroValueChanged}
                  value={state.sampleFormatOptions.showZeroValuesAs}
                />
              </>
            )}
            {state.selectedFormatType === CustomFormatOption.Percentage && (
              <>
                <FormControlLabel
                  sx={{ mt: "-5px" }}
                  control={<Checkbox checked={state.sampleFormatOptions.convertToPercentage === true} />}
                  label={<Typography>Convert to Percentage</Typography>}
                  onChange={(_, value) => handleConvertToPercentageChange(value)}
                />
                <Box sx={{ display: "flex", pt: ".5rem", gap: ".5rem" }}>
                  <Decimals formatOptions={state.sampleFormatOptions} applyFormat={handleDecimalsChange} />
                </Box>
              </>
            )}
            {state.selectedFormatType === CustomFormatOption.Currency && (
              <CurrencyCustomFormat
                referenceDimensionName={state.sampleFormatOptions.referenceDimensionName}
                currencySource={state.sampleFormatOptions.currencySource}
                currencySymbol={state.sampleFormatOptions.currency}
                sampleFormatOptions={state.sampleFormatOptions}
                onReferenceDimensionNameChange={handleReferenceDimensionNameChange}
                onAbbreviationChange={handleAbbreviationChange}
                onDecimalsChange={handleDecimalsChange}
                onCurrencySourceChange={handleCurrencySourceChange}
                onCurrencySymbolChange={handleCurrencySymbolChange}
                currencyDimensions={currencyDimensions}
                onNegativeValueChanged={handleNegativeValueChanged}
                onZeroValueChanged={handleZeroValueChanged}
              />
            )}
          </Stack>
        </Box>
        <Divider />
      </DialogContent>
      <DialogButtons onCancel={close}>
        <Button
          variant="contained"
          onClick={() => {
            applyFormat(state.sampleFormatOptions);
            close();
          }}
        >
          Apply
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default MeasureCustomFormatPopup;
